import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Mail,
  ArrowLeft,
  User,
  DollarSign,
  Save,
  MapPin,
  Phone,
  Globe,
  Clock,
  CheckCircle,
  XCircle,
  Bell,
  BellOff,
  Trash2,
  AlertTriangle,
  Download,
  Loader2
} from "lucide-react";

export default function ProfilePage() {
  const { user, token, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [formData, setFormData] = useState({
    name: user?.name || "",
    payment_method: user?.payment_method || "",
    paypal_email: user?.paypal_email || ""
  });
  const [notificationPrefs, setNotificationPrefs] = useState({
    email_new_tasks: true,
    email_task_approved: true,
    email_task_rejected: true,
    email_payment_processed: true,
    email_weekly_summary: false,
    email_promotional: false
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    // Fetch notification preferences
    const fetchNotificationPrefs = async () => {
      try {
        const response = await axios.get(`${API}/v2/users/notification-preferences`, { headers });
        setNotificationPrefs(response.data);
      } catch (error) {
        console.log("Could not fetch notification preferences");
      }
    };
    if (token) fetchNotificationPrefs();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.patch(`${API}/v2/users/profile`, formData, { headers });
      updateUser(response.data);
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationToggle = async (key, value) => {
    const newPrefs = { ...notificationPrefs, [key]: value };
    setNotificationPrefs(newPrefs);
    setNotifLoading(true);
    
    try {
      await axios.patch(`${API}/v2/users/notification-preferences`, newPrefs, { headers });
      toast.success("Notification preferences updated");
    } catch (error) {
      // Revert on error
      setNotificationPrefs(notificationPrefs);
      toast.error("Failed to update preferences");
    } finally {
      setNotifLoading(false);
    }
  };

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${API}/v2/users/me/export-data`, { headers });
      // Create and download JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `gigseasy-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Your data has been exported");
    } catch (error) {
      toast.error("Failed to export data");
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== "DELETE MY ACCOUNT") {
      toast.error("Please type 'DELETE MY ACCOUNT' to confirm");
      return;
    }
    
    setDeleteLoading(true);
    try {
      await axios.post(`${API}/v2/users/me/delete-account`, {
        password: deletePassword,
        confirmation: deleteConfirmation
      }, { headers });
      
      toast.success("Your account has been deleted");
      logout();
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete account");
    } finally {
      setDeleteLoading(false);
    }
  };

  const getDashboardLink = () => {
    if (user?.role === "admin") return "/admin";
    if (user?.role === "team_manager") return "/team-manager";
    return "/dashboard";
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="profile-page">
      {/* Header - Mobile optimized */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                src="/images/gigseasy-logo.svg" 
                alt="GigsEasy Logo" 
                className="h-8 sm:h-10"
              />
              <div>
                <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">Profile Settings</p>
              </div>
            </div>
            <Link to={getDashboardLink()}>
              <Button variant="ghost" className="h-9 sm:h-10 px-2 sm:px-4" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Back to Dashboard</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 p-4 sm:p-6">
            <CardTitle className="font-heading text-lg sm:text-xl flex items-center gap-2">
              <User className="w-4 h-4 sm:w-5 sm:h-5" />
              Profile Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div className="space-y-2">
                <Label className="text-sm">Email</Label>
                <Input
                  value={user?.email || ""}
                  disabled
                  className="bg-slate-50 h-11"
                />
                <p className="text-xs sm:text-sm text-slate-500">Email cannot be changed</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm">Full Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="h-11"
                  data-testid="profile-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Role</Label>
                <Input
                  value={user?.role?.replace("_", " ") || ""}
                  disabled
                  className="bg-slate-50 capitalize h-11"
                />
              </div>

              <div className="border-t border-slate-200 pt-4 sm:pt-6 mt-4 sm:mt-6">
                <h3 className="font-heading font-semibold text-base sm:text-lg mb-3 sm:mb-4 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
                  Payment Preferences
                </h3>

                {/* Payment Approval Status Banner - Mobile optimized */}
                {user?.payment_approval_status === "pending" && (
                  <div className="mb-4 p-3 sm:p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 sm:gap-3">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-800 text-sm sm:text-base">Payment Details Pending Approval</p>
                      <p className="text-xs sm:text-sm text-amber-700 mt-1">
                        Your payment detail changes are awaiting admin approval.
                      </p>
                      {user?.pending_payment_details && (
                        <div className="mt-2 text-xs sm:text-sm text-amber-700">
                          <p className="font-medium">Requested changes:</p>
                          <ul className="list-disc list-inside mt-1">
                            {user.pending_payment_details.payment_method && (
                              <li>Method: {user.pending_payment_details.payment_method}</li>
                            )}
                            {user.pending_payment_details.paypal_email && (
                              <li>PayPal: {user.pending_payment_details.paypal_email}</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {user?.payment_approval_status === "rejected" && (
                  <div className="mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 sm:gap-3">
                    <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-red-800 text-sm sm:text-base">Payment Details Change Rejected</p>
                      <p className="text-xs sm:text-sm text-red-700 mt-1">
                        Your recent payment detail change was not approved. Please contact admin.
                      </p>
                    </div>
                  </div>
                )}

                {user?.payment_approval_status === "approved" && (
                  <div className="mb-4 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2 sm:gap-3">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-green-800 text-sm sm:text-base">Payment Details Approved</p>
                      <p className="text-xs sm:text-sm text-green-700 mt-1">
                        Your payment details have been verified and approved.
                      </p>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="payment_method" className="text-sm">Preferred Payment Method</Label>
                    <Select 
                      value={formData.payment_method || ""} 
                      onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                      disabled={user?.payment_approval_status === "pending"}
                    >
                      <SelectTrigger data-testid="payment-method-select" className={`h-11 ${user?.payment_approval_status === "pending" ? "opacity-60" : ""}`}>
                        <SelectValue placeholder="Select payment method" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paypal">PayPal</SelectItem>
                        <SelectItem value="custom">Custom (Contact Admin)</SelectItem>
                      </SelectContent>
                    </Select>
                    {user?.payment_approval_status === "pending" && (
                      <p className="text-xs text-amber-600">Cannot modify while approval is pending</p>
                    )}
                  </div>

                  {formData.payment_method === "paypal" && (
                    <div className="space-y-2">
                      <Label htmlFor="paypal_email" className="text-sm">PayPal Email</Label>
                      <Input
                        id="paypal_email"
                        type="email"
                        value={formData.paypal_email}
                        onChange={(e) => setFormData({ ...formData, paypal_email: e.target.value })}
                        placeholder="your@paypal.com"
                        disabled={user?.payment_approval_status === "pending"}
                        className={`h-11 ${user?.payment_approval_status === "pending" ? "opacity-60" : ""}`}
                        data-testid="paypal-email-input"
                      />
                    </div>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11"
                data-testid="save-profile-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Account Stats - Mobile optimized */}
        <Card className="border-slate-200 mt-4 sm:mt-6">
          <CardHeader className="border-b border-slate-100 p-4 sm:p-6">
            <CardTitle className="font-heading text-lg sm:text-xl">Account Stats</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              <div>
                <p className="text-xs sm:text-sm text-slate-500 mb-1">Points Balance</p>
                <p className="text-lg sm:text-2xl font-heading font-bold text-amber-600">
                  {user?.points_balance?.toLocaleString() || user?.points?.toLocaleString() || 0} pts
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-slate-500 mb-1">Account Status</p>
                <p className={`text-lg sm:text-2xl font-heading font-bold capitalize ${
                  user?.status === "active" ? "text-green-600" : 
                  user?.status === "pending" ? "text-amber-600" : "text-red-600"
                }`}>
                  {user?.status}
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-slate-500 mb-1">Member Since</p>
                <p className="text-sm sm:text-lg font-medium text-slate-900">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-slate-500 mb-1">Role</p>
                <p className="text-sm sm:text-lg font-medium text-slate-900 capitalize">
                  {user?.role?.replace("_", " ")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Personal Details - Mobile optimized */}
        {(user?.address || user?.contact_info) && (
          <Card className="border-slate-200 mt-4 sm:mt-6">
            <CardHeader className="border-b border-slate-100 p-4 sm:p-6">
              <CardTitle className="font-heading text-lg sm:text-xl flex items-center gap-2">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
                Personal Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Address Section */}
              {user?.address && (
                <div>
                  <h4 className="font-medium text-slate-700 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                    <Globe className="w-3 h-3 sm:w-4 sm:h-4" />
                    Address
                  </h4>
                  <div className="bg-slate-50 rounded-lg p-3 sm:p-4 space-y-1 sm:space-y-2">
                    {user.address.street && (
                      <p className="text-slate-600 text-sm sm:text-base">{user.address.street}</p>
                    )}
                    <p className="text-slate-600">
                      {[user.address.city, user.address.zip_code].filter(Boolean).join(", ")}
                    </p>
                    {user.address.country && (
                      <p className="text-slate-900 font-medium text-sm sm:text-base">{user.address.country}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Information Section */}
              {user?.contact_info && (
                <div>
                  <h4 className="font-medium text-slate-700 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                    <Phone className="w-3 h-3 sm:w-4 sm:h-4" />
                    Contact Information
                  </h4>
                  <div className="bg-slate-50 rounded-lg p-3 sm:p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {(user.contact_info.phone_area_code || user.contact_info.phone) && (
                        <div>
                          <p className="text-xs sm:text-sm text-slate-500 mb-0.5 sm:mb-1">Phone</p>
                          <p className="text-slate-900 text-sm sm:text-base">
                            {user.contact_info.phone_area_code} {user.contact_info.phone}
                          </p>
                        </div>
                      )}
                      {user.contact_info.whatsapp && (
                        <div>
                          <p className="text-xs sm:text-sm text-slate-500 mb-0.5 sm:mb-1">WhatsApp</p>
                          <p className="text-slate-900 text-sm sm:text-base">{user.contact_info.whatsapp}</p>
                        </div>
                      )}
                      {user.contact_info.telegram && (
                        <div>
                          <p className="text-xs sm:text-sm text-slate-500 mb-0.5 sm:mb-1">Telegram</p>
                          <p className="text-slate-900 text-sm sm:text-base">{user.contact_info.telegram}</p>
                        </div>
                      )}
                      {user.contact_info.instagram && (
                        <div>
                          <p className="text-xs sm:text-sm text-slate-500 mb-0.5 sm:mb-1">Instagram</p>
                          <p className="text-slate-900 text-sm sm:text-base">{user.contact_info.instagram}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Notification Preferences */}
        <Card className="border-slate-200 mt-4 sm:mt-6">
          <CardHeader className="border-b border-slate-100 p-4 sm:p-6">
            <CardTitle className="font-heading text-lg sm:text-xl flex items-center gap-2">
              <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
              Notification Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <div className="space-y-4">
              {[
                { key: "email_new_tasks", label: "New Tasks Available", description: "Get notified when new tasks are available for you" },
                { key: "email_task_approved", label: "Task Approved", description: "Get notified when your task submission is approved" },
                { key: "email_task_rejected", label: "Task Rejected", description: "Get notified when your task submission is rejected" },
                { key: "email_payment_processed", label: "Payment Processed", description: "Get notified when your withdrawal is processed" },
                { key: "email_weekly_summary", label: "Weekly Summary", description: "Receive a weekly summary of your activity and earnings" },
                { key: "email_promotional", label: "Promotional Emails", description: "Receive news about new features and promotions" }
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div>
                    <p className="font-medium text-slate-900 text-sm sm:text-base">{item.label}</p>
                    <p className="text-xs sm:text-sm text-slate-500">{item.description}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleNotificationToggle(item.key, !notificationPrefs[item.key])}
                    disabled={notifLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      notificationPrefs[item.key] ? 'bg-[#00B894]' : 'bg-slate-300'
                    } ${notifLoading ? 'opacity-50' : ''}`}
                    data-testid={`notif-toggle-${item.key}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        notificationPrefs[item.key] ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Data Management & Account Deletion */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading flex items-center gap-2 text-lg sm:text-xl">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Data & Account Management
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 space-y-6">
            {/* Data Export */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h4 className="font-medium text-blue-800">Export Your Data</h4>
                  <p className="text-sm text-blue-600 mt-1">
                    Download a copy of all your personal data stored with GigsEasy (GDPR compliance)
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportData}
                  disabled={exportLoading}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  data-testid="export-data-btn"
                >
                  {exportLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export
                </Button>
              </div>
            </div>

            {/* Delete Account */}
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-start gap-3">
                <Trash2 className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-medium text-red-800">Delete Account</h4>
                  <p className="text-sm text-red-600 mt-1">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                  
                  {!showDeleteConfirm ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="mt-3 border-red-300 text-red-700 hover:bg-red-100"
                      data-testid="show-delete-btn"
                    >
                      Delete My Account
                    </Button>
                  ) : (
                    <div className="mt-4 space-y-4 p-4 bg-white rounded-lg border border-red-200">
                      <div className="flex items-center gap-2 text-amber-700 bg-amber-50 p-3 rounded-lg">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm font-medium">Warning: This will permanently delete all your data</span>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-sm text-slate-700">Enter your password</Label>
                        <Input
                          type="password"
                          value={deletePassword}
                          onChange={(e) => setDeletePassword(e.target.value)}
                          placeholder="Your current password"
                          className="h-10"
                          data-testid="delete-password-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-sm text-slate-700">
                          Type <span className="font-mono font-bold text-red-600">DELETE MY ACCOUNT</span> to confirm
                        </Label>
                        <Input
                          type="text"
                          value={deleteConfirmation}
                          onChange={(e) => setDeleteConfirmation(e.target.value)}
                          placeholder="DELETE MY ACCOUNT"
                          className="h-10 font-mono"
                          data-testid="delete-confirmation-input"
                        />
                      </div>
                      
                      <div className="flex gap-3 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowDeleteConfirm(false);
                            setDeletePassword("");
                            setDeleteConfirmation("");
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleDeleteAccount}
                          disabled={deleteLoading || deleteConfirmation !== "DELETE MY ACCOUNT"}
                          data-testid="confirm-delete-btn"
                        >
                          {deleteLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4 mr-2" />
                          )}
                          Permanently Delete Account
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
